import html2canvas from 'html2canvas'
import type {
  TFirstPageRenderOptions,
  TFirstPageRenderResult,
  TFirstPageResolver,
  TFirstPageSlice,
} from '@/types/thumbnails'

/** Cap on waiting for an image that has not loaded yet; the sandbox is off-screen, where decode() can stay pending. */
const IMAGE_DECODE_TIMEOUT_MS = 2000

/**
 * FNV-1a over the render inputs. Not a crypto hash on purpose: crypto.subtle
 * only exists in a secure context, and dev is served over plain http.
 */
function signatureOf(parts: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < parts.length; i++) {
    hash ^= parts.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }

  return `${parts.length.toString(36)}-${(hash >>> 0).toString(36)}`
}

/** Content children of the root that start above the cutoff, in document order. */
function contentAbove(root: HTMLElement, cutoff: number, skip: (el: Element) => boolean): Element[] {
  const rootTop = root.getBoundingClientRect().top
  const nodes: Element[] = []

  for (const child of root.children) {
    if (skip(child)) {
      continue
    }
    if (child.getBoundingClientRect().top - rootTop >= cutoff) {
      break
    }
    nodes.push(child)
  }

  // a first child taller than the page still has to be rendered
  if (!nodes.length) {
    const first = [...root.children].find((child) => !skip(child))
    if (first) {
      nodes.push(first)
    }
  }

  return nodes
}

/** First page of a Tiptap document with no pagination: whatever starts within one page height. */
export const plainTiptapFirstPage: TFirstPageResolver = (root, pageHeight) => ({
  structuralNodes: [],
  contentNodes: contentAbove(root, pageHeight, () => false),
})

/**
 * First page of a Tiptap document paginated by the pages extension. The page
 * frames live in a zero-height decoration that has to be cloned whole: its
 * breakers carry the page margins, so a partial copy loses the top margin and
 * shifts the whole page up.
 */
export const tiptapPagesFirstPage: TFirstPageResolver = (root, pageHeight) => {
  const decoration = root.querySelector(':scope > .ProseMirror-widget')
  const pageFrames = decoration ? [...decoration.querySelectorAll('.tiptap-page-break .page')] : []

  if (!decoration || pageFrames.length < 2) {
    return plainTiptapFirstPage(root, pageHeight)
  }

  const cutoff = pageFrames[1]!.getBoundingClientRect().top - root.getBoundingClientRect().top

  return {
    structuralNodes: [decoration],
    contentNodes: contentAbove(root, cutoff, (el) => el === decoration),
  }
}

async function inlineImages(root: HTMLElement, inlineImage?: (url: string) => Promise<string | null>): Promise<void> {
  const images = [...root.querySelectorAll('img')]
  if (!images.length) {
    return
  }

  await Promise.all(
    images.map(async (img) => {
      img.removeAttribute('loading')
      if (!inlineImage || !img.src || img.src.startsWith('data:')) {
        return
      }
      const dataUrl = await inlineImage(img.src)
      if (dataUrl && dataUrl.startsWith('data:')) {
        img.src = dataUrl
      }
    }),
  )
  const pending = images.filter((img) => !img.complete || !img.naturalWidth)
  if (!pending.length) {
    return
  }

  await Promise.race([
    Promise.all(pending.map((img) => img.decode().catch(() => undefined))),
    new Promise((resolve) => setTimeout(resolve, IMAGE_DECODE_TIMEOUT_MS)),
  ])
}

/**
 * Render the first page of an editor document to a JPEG.
 *
 * Only the first page's nodes are cloned, so the cost does not grow with the
 * length of the document: html2canvas walks and paints every node of whatever
 * it is given, and its x/y/width/height options crop the output canvas only.
 */
export async function renderFirstPage(options: TFirstPageRenderOptions): Promise<TFirstPageRenderResult> {
  const {
    root,
    pageWidth,
    pageHeight,
    resolveFirstPage,
    inlineImage,
    cssVars,
    maxWidth = 448,
    upscale = false,
    quality = 0.85,
    previousSignature,
  } = options

  if (!pageWidth || !pageHeight) {
    return { status: 'unavailable' }
  }

  const slice: TFirstPageSlice | null = resolveFirstPage(root, pageHeight)
  if (!slice || !slice.contentNodes.length) {
    return { status: 'unavailable' }
  }

  const signature = signatureOf(
    JSON.stringify([
      slice.contentNodes.map((node) => node.outerHTML).join(''),
      pageWidth,
      pageHeight,
      maxWidth,
      quality,
      cssVars ?? {},
    ]),
  )

  if (previousSignature && previousSignature === signature) {
    return { status: 'unchanged', signature }
  }

  if (document.fonts?.ready) {
    await document.fonts.ready
  }

  const renderRoot = root.cloneNode(false) as HTMLElement
  renderRoot.style.margin = '0'
  renderRoot.style.transform = 'none'
  for (const node of [...slice.structuralNodes, ...slice.contentNodes]) {
    renderRoot.appendChild(node.cloneNode(true))
  }

  const sandbox = document.createElement('div')
  sandbox.className = 'preview'
  sandbox.style.cssText = `position: absolute; top: 0; left: -10000px; width: ${pageWidth}px; background: white;`
  for (const [name, value] of Object.entries(cssVars ?? {})) {
    sandbox.style.setProperty(name, value)
  }
  sandbox.appendChild(renderRoot)
  document.body.appendChild(sandbox)

  try {
    await inlineImages(renderRoot, inlineImage)

    const canvas = await html2canvas(renderRoot, {
      backgroundColor: '#ffffff',
      scale: upscale ? maxWidth / pageWidth : Math.min(1, maxWidth / pageWidth),
      width: pageWidth,
      height: pageHeight,
      x: 0,
      y: 0,
      useCORS: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
    })

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
    if (!blob) {
      return { status: 'unavailable' }
    }

    return { status: 'rendered', blob, signature }
  } finally {
    sandbox.remove()
  }
}
